/** Merge these keys under presentations.guided in each locale. */
export const guidedCreateCopy = {
  en: {
    title: 'New presentation',
    description:
      'Create a presentation in this project, quickly or starting from a brief and a style.',
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    create: 'Create presentation',
    creating: 'Creating presentation…',
    mode: {
      label: 'Creation path',
      quick: 'Quick create',
      guided: 'Guided',
      quickHint: 'Name it and start. Both fields are saved.',
      guidedHint: 'Start from a brief and explore a style before you create.',
    },
    steps: {
      label: 'Guided steps',
      progress: 'Step {{current}} of {{total}}: {{name}}',
      brief: 'Brief',
      style: 'Style',
      review: 'Review',
    },
    brief: {
      heading: 'Describe the presentation',
      intro:
        'The name and description are saved. Purpose and slide count only guide the style handoff.',
      name: 'Presentation name',
      topic: 'Purpose or topic',
      topicHelp:
        'What is it for and who is it for? Optional. Prefills the ChatGPT handoff if you choose a style; not saved.',
      description: 'Description',
      descriptionHelp: 'Optional. Saved with the presentation.',
      slideCount: 'Target slide count',
      slideCountHelp: 'Optional, 1 to 100. A planning hint; not saved.',
      counter: '{{count}} / {{max}}',
    },
    issue: {
      required: 'Enter a presentation name.',
      tooLong: 'Too long. Shorten this to {{max}} characters or fewer.',
      notWhole: 'Enter a whole number.',
      outOfRange: 'Enter a number between 1 and 100.',
      rejected: 'This value was not accepted. Check it and try again.',
    },
    style: {
      heading: 'Explore a style',
      intro:
        'Look through the styles available to this project. Choosing one here is a note for the review and the handoff. It does not apply the style.',
      optional: 'You can skip this step.',
      chosen: 'Noted for review: {{name}}',
      none: 'No style noted',
      clear: 'Clear style',
      unknownSelection: 'The style in the link is not available to this project and was ignored.',
    },
    review: {
      heading: 'Review before creating',
      intro: 'Check what will be saved. Nothing has been created yet.',
      savedHeading: 'Will be saved',
      guidanceHeading: 'Guidance only. Not saved',
      projectName: 'Name',
      projectDescription: 'Description',
      empty: 'Not provided',
      topic: 'Purpose or topic',
      slideCount: 'Target slide count',
      style: 'Style direction',
      styleNone: 'None chosen',
      styleNote:
        'Creating does not apply this style. It stays a reference for the ChatGPT handoff until styles can be applied to a presentation.',
      themeHeading: 'Theme',
      themeInherited:
        'The theme is managed for the whole project and is not chosen here. Creating does not change it.',
      themeLoading: 'Loading the project theme',
      themeUnavailable: 'The project theme could not be shown. Creating is not affected.',
      themeLink: 'Open project theme settings',
      themeManagerOnly: 'Only project managers can change the theme.',
      themeColors: 'Project theme colors',
      handoffHeading: 'Take it to ChatGPT',
      handoffHint: 'Opens a reviewable prompt built from your brief and the chosen style.',
    },
    errors: {
      validation: 'Some details were rejected. Check the fields and try again.',
      denied: 'You do not have permission to create presentations in this project.',
      missingProject: 'This project is unavailable or no longer exists.',
      session: 'Your session has ended. Sign in again, then retry. Your input is kept here.',
      retryable: 'The presentation could not be created. Your input is kept. Try again.',
      retry: 'Try again',
      backToBrief: 'Edit brief',
    },
    created: {
      title: 'The presentation was created, but we could not open it.',
      open: 'Open the new presentation',
    },
    loadErrors: {
      missingProject: 'This project is unavailable or no longer exists.',
      denied: 'You do not have access to this project.',
      session: 'Your session has ended. Sign in again.',
      retryable: 'The project could not be loaded. Try again.',
    },
    leave: {
      title: 'Discard this brief?',
      description: 'You have entered details that have not been used to create a presentation.',
      stay: 'Keep editing',
      discard: 'Discard and leave',
    },
    unsaved: 'Not saved yet. Leaving this page discards your input.',
  },
  fa: {
    title: 'ارائهٔ جدید',
    description: 'در این پروژه سریع یا با شروع از یک خلاصه و یک سبک، ارائه بسازید.',
    cancel: 'انصراف',
    back: 'قبلی',
    next: 'بعدی',
    create: 'ساخت ارائه',
    creating: 'در حال ساخت ارائه…',
    mode: {
      label: 'روش ساخت',
      quick: 'ساخت سریع',
      guided: 'راهنما‌دار',
      quickHint: 'نام بدهید و شروع کنید. هر دو فیلد ذخیره می‌شوند.',
      guidedHint: 'با یک خلاصه شروع کنید و پیش از ساخت، سبک‌ها را ببینید.',
    },
    steps: {
      label: 'مراحل راهنما',
      progress: 'مرحلهٔ {{current}} از {{total}}: {{name}}',
      brief: 'خلاصه',
      style: 'سبک',
      review: 'بازبینی',
    },
    brief: {
      heading: 'ارائه را توصیف کنید',
      intro: 'نام و توضیح ذخیره می‌شوند. هدف و تعداد اسلاید فقط برای راهنمایی انتقال سبک هستند.',
      name: 'نام ارائه',
      topic: 'هدف یا موضوع',
      topicHelp:
        'برای چه کسی و برای چه؟ اختیاری. اگر سبکی انتخاب کنید، انتقال به ChatGPT را پر می‌کند؛ ذخیره نمی‌شود.',
      description: 'توضیح',
      descriptionHelp: 'اختیاری. همراه ارائه ذخیره می‌شود.',
      slideCount: 'تعداد اسلاید هدف',
      slideCountHelp: 'اختیاری، ۱ تا ۱۰۰. فقط برای برنامه‌ریزی؛ ذخیره نمی‌شود.',
      counter: '{{count}} / {{max}}',
    },
    issue: {
      required: 'نام ارائه را وارد کنید.',
      tooLong: 'خیلی طولانی است. حداکثر {{max}} نویسه مجاز است.',
      notWhole: 'یک عدد صحیح وارد کنید.',
      outOfRange: 'عددی بین ۱ تا ۱۰۰ وارد کنید.',
      rejected: 'این مقدار پذیرفته نشد. آن را بررسی و دوباره تلاش کنید.',
    },
    style: {
      heading: 'بررسی سبک',
      intro:
        'سبک‌های در دسترس این پروژه را ببینید. انتخاب در اینجا فقط یادداشتی برای بازبینی و انتقال است و سبک را اعمال نمی‌کند.',
      optional: 'می‌توانید از این مرحله بگذرید.',
      chosen: 'یادداشت‌شده برای بازبینی: {{name}}',
      none: 'سبکی یادداشت نشده',
      clear: 'پاک کردن سبک',
      unknownSelection: 'سبکِ موجود در پیوند برای این پروژه در دسترس نیست و نادیده گرفته شد.',
    },
    review: {
      heading: 'بازبینی پیش از ساخت',
      intro: 'آنچه ذخیره می‌شود را بررسی کنید. هنوز چیزی ساخته نشده است.',
      savedHeading: 'ذخیره می‌شود',
      guidanceHeading: 'فقط راهنما. ذخیره نمی‌شود',
      projectName: 'نام',
      projectDescription: 'توضیح',
      empty: 'وارد نشده',
      topic: 'هدف یا موضوع',
      slideCount: 'تعداد اسلاید هدف',
      style: 'جهت سبک',
      styleNone: 'انتخاب نشده',
      styleNote:
        'ساخت ارائه این سبک را اعمال نمی‌کند. تا زمانی که امکان اعمال سبک فراهم شود، فقط مرجعی برای انتقال به ChatGPT است.',
      themeHeading: 'پوسته',
      themeInherited:
        'پوسته برای کل پروژه مدیریت می‌شود و اینجا انتخاب نمی‌شود. ساخت ارائه آن را تغییر نمی‌دهد.',
      themeLoading: 'در حال بارگذاری پوستهٔ پروژه',
      themeUnavailable: 'پوستهٔ پروژه نمایش داده نشد. ساخت ارائه تحت تأثیر نیست.',
      themeLink: 'باز کردن تنظیمات پوستهٔ پروژه',
      themeManagerOnly: 'فقط مدیران پروژه می‌توانند پوسته را تغییر دهند.',
      themeColors: 'رنگ‌های پوستهٔ پروژه',
      handoffHeading: 'انتقال به ChatGPT',
      handoffHint: 'یک متن قابل بازبینی از خلاصه و سبک انتخابی شما می‌سازد.',
    },
    errors: {
      validation: 'برخی جزئیات پذیرفته نشد. فیلدها را بررسی و دوباره تلاش کنید.',
      denied: 'اجازهٔ ساخت ارائه در این پروژه را ندارید.',
      missingProject: 'این پروژه در دسترس نیست یا دیگر وجود ندارد.',
      session:
        'نشست شما پایان یافته است. دوباره وارد شوید و تلاش کنید. ورودی‌های شما اینجا نگه داشته شده است.',
      retryable: 'ارائه ساخته نشد. ورودی‌های شما نگه داشته شده است. دوباره تلاش کنید.',
      retry: 'تلاش دوباره',
      backToBrief: 'ویرایش خلاصه',
    },
    created: { title: 'ارائه ساخته شد، اما باز نشد.', open: 'باز کردن ارائهٔ جدید' },
    loadErrors: {
      missingProject: 'این پروژه در دسترس نیست یا دیگر وجود ندارد.',
      denied: 'به این پروژه دسترسی ندارید.',
      session: 'نشست شما پایان یافته است. دوباره وارد شوید.',
      retryable: 'پروژه بارگذاری نشد. دوباره تلاش کنید.',
    },
    leave: {
      title: 'این خلاصه کنار گذاشته شود؟',
      description: 'جزئیاتی وارد کرده‌اید که هنوز برای ساخت ارائه استفاده نشده است.',
      stay: 'ادامهٔ ویرایش',
      discard: 'کنار گذاشتن و خروج',
    },
    unsaved: 'هنوز ذخیره نشده است. خروج از این صفحه ورودی‌های شما را حذف می‌کند.',
  },
} as const;
